/**
 * Helpers for the `attemptAutoLogin` deep-link flow.
 *
 * A public service deep link such as
 *   /services/:id?attemptAutoLogin=1&kc_idp_hint=ndi
 * should actively start a Keycloak login (optionally brokered straight to an
 * identity provider) instead of loading the form anonymously. The `services/:id`
 * route is unguarded and Keycloak boots with `onLoad: 'check-sso'`, which never
 * forces a login, so without this the trigger params are silently ignored.
 */

import { removeQueryParams } from './string-utils';

const AUTO_LOGIN_PARAM = 'attemptAutoLogin';
const IDP_HINT_PARAM = 'kc_idp_hint';

/** Query params consumed by the auto-login flow; never round-tripped back. */
const AUTO_LOGIN_QUERY_PARAMS = [AUTO_LOGIN_PARAM, IDP_HINT_PARAM];

/**
 * Conservative shape for an IdP hint (a Keycloak identity-provider alias).
 * Rejects whitespace, slashes and other characters that have no business in an
 * alias, neutralizing redirect/injection-style values from a crafted URL.
 *
 * This is input hygiene, not IdP policy: which IdPs exist is owned entirely by
 * Keycloak. `kc_idp_hint` can only select a provider already configured in the
 * realm, and Keycloak ignores an unrecognized hint — so no frontend allowlist
 * is needed (it would only duplicate Keycloak config and risk silent breakage).
 */
const SAFE_IDP_HINT = /^[a-zA-Z0-9_-]{1,64}$/;

export interface AutoLoginIntent {
  /** Whether an unauthenticated visitor should be redirected to login. */
  shouldLogin: boolean;
  /** Validated Keycloak `idpHint`, if a well-formed one was supplied. */
  idpHint?: string;
}

/**
 * Decides what an `attemptAutoLogin` deep link is asking for.
 *
 * @param search The URL query string (with or without a leading `?`).
 */
export function resolveAutoLoginIntent(search: string): AutoLoginIntent {
  const params = new URLSearchParams(search || '');

  const flag = params.get(AUTO_LOGIN_PARAM);
  const shouldLogin = flag === '1' || flag === 'true';
  if (!shouldLogin) {
    return { shouldLogin: false };
  }

  const rawHint = params.get(IDP_HINT_PARAM);
  if (rawHint && SAFE_IDP_HINT.test(rawHint)) {
    return { shouldLogin: true, idpHint: rawHint };
  }

  return { shouldLogin: true };
}

/**
 * Strips the auto-login trigger params (and any Keycloak `code`/`state`) from a
 * URL so the post-login `redirectUri` cannot re-trigger the flow into a loop.
 */
export function stripAutoLoginParams(url: string): string {
  return removeQueryParams(url, ['code', 'state', ...AUTO_LOGIN_QUERY_PARAMS]);
}
